import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors } from '@angular/forms';
import {
    Component,
    ElementRef,
    EventEmitter,
    forwardRef,
    inject,
    Input,
    Output,
    Renderer2,
    ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';

import { ButtonComponent } from '../button/button.component';
import { MONTH_NAMES, WEEKDAYS } from './constants/date-consts';
import { MaskedInputDirective } from '../../directives/textmask.directive';
import { TimeSelectComponent, TimeSelected } from './time-select/time-select.component';
import { DatePickerHeaderComponent, DatePickerHeaderType } from './date-picker-header/date-picker-header.component';

export enum DatePickerType {
    SINGLE = 'singleCalendar',
    DOUBLE = 'doubleCalendar'
}

export interface CalendarDays {
    date: Date;
    neutral: boolean;
    current?: boolean;
    isDayInRange?: boolean;
    isRangeStart?: boolean;
    isRangeEnd?: boolean;
    hasActiveEvent?: boolean;
}

export interface DianDatePickerRange {
    start: Date,
    end: Date ,
}

@Component({
    selector: 'dian-date-picker',
    standalone: true,
    imports: [
        TimeSelectComponent,
        MaskedInputDirective,
        CommonModule,
        DatePickerHeaderComponent,
        ButtonComponent,
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DatePickerComponent),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => DatePickerComponent),
            multi: true
        }
    ],
    templateUrl: './date-picker.component.html',
    styleUrls: ['./date-picker.component.scss']
})
export class DatePickerComponent implements ControlValueAccessor {
    @ViewChild('dateInput') inputElement!: ElementRef<HTMLInputElement>;
    @ViewChild('datePicker') datePicker!: ElementRef<HTMLDivElement>;

    @Input() errorMessage!: string;
    @Input() isRange = false;
    @Input() calendarType: DatePickerType = DatePickerType.SINGLE;
    @Input() headerType: DatePickerHeaderType = DatePickerHeaderType.RIGHT_DROPDOWN;
    @Input() activeEvents: string[] = [];
    @Input() set maxDate (date: string) {
        this.maxDateLimit = new Date(date);
        if (isNaN(this.maxDateLimit.getTime())) throw Error (`el ${date} ingresado para maxDate no es valido`);
    }
    @Input() set minDate (date: string) {
        this.minDateLimit = new Date(date);
        if (isNaN(this.minDateLimit.getTime())) throw Error (`el ${date} ingresado para minDate no es valido`);
    }

    @Output() dateSelected = new EventEmitter<string>;

    value!: null | string;
    selectedDateString: string = '';
    selectedRangeStartDateString: string = '';
    selectedRangeEndDateString: string = '';
    calendarVisible: boolean = false;

    weekdays: string[] = WEEKDAYS;
    monthNames: string[] = MONTH_NAMES;
    currentDate = new Date();

    selectedDate!: Date | null;
    currentMonth!: number;
    currentYear!: number;
    currentDayDate!: number;
    calendarDays: CalendarDays[] = [];
    numDaysInRow = 7;
    dayButtons!: NodeListOf<HTMLButtonElement>;
    hasInputError = false;
    isDisabled = false;
    timeSelected!: Omit<TimeSelected, 'format'>;
    DatePickerType = DatePickerType;
    isDisabledSaveButton = true;

    dayViewActive = true;
    yearsOptions: number[] = [];
    maxYearsOptionsCount = 14;
    monthYearViewTarget: 'monthView' | 'yearView' = 'monthView'
    rangeSelected = { } as DianDatePickerRange;
    assignedFormControl!: AbstractControl;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onChange: (value: unknown) => void = () => {};
    onTouch: () => void = () => {};

    leftCalendarDays!: CalendarDays[];
    maxDateLimit!: Date;
    minDateLimit!: Date;

    private renderer = inject(Renderer2);

    constructor() {
        this.currentMonth = this.currentDate.getMonth();
        this.currentYear = this.currentDate.getFullYear();
        this.currentDayDate = this.currentDate.getDate();
        this.autoCloseCalendarModal();
        this.setYearsOptions(this.currentYear);
    }

    validate(control: AbstractControl): ValidationErrors | null {
        this.assignedFormControl = control;
        this.hasInputError = (Object.keys(control.errors || {}).length !== 0) && (control.touched || control.dirty);
        return null;
    }

    writeValue(value: string): void {
        if(!value) return;
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
            this.onChange(date);
            this.value = date.toUTCString();
            this.selectedDateString = date.toLocaleDateString("en-GB");
            this.timeSelected = {
                hours: date.getHours(),
                minutes: date.getMinutes(),
                seconds: date.getSeconds()
            }
        }
    }

    registerOnChange(fn: (value: unknown) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouch = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
    }

    toggleCalendar() {
        this.calendarVisible = !this.calendarVisible;
        this.dayViewActive = this.calendarVisible;
        this.selectedDateString = this.inputElement.nativeElement.value;
        const isInvalidInput = this.inputElement.nativeElement.classList.contains('invalid');
        const date = this.parseDate(this.selectedDateString);

        if (date && this.calendarVisible && !isInvalidInput) {
            this.setActiveDate(date);
            if (!this.isRange) this.selectDate(date)
        } else {
            this.setActiveDate();
        }

        this.calendarDays = [];
        this.leftCalendarDays = []
        this.calendarDays = this.populateCalendar(this.currentMonth, this.currentYear);
        this.leftCalendarDays = this.populateCalendar(this.currentMonth + 1 > 11 ? 0 : this.currentMonth + 1, this.currentYear);
        this.markDaysInRange(this.rangeSelected);

    }

    prevMonth() {
        if (!this.dayViewActive && this.monthYearViewTarget === 'yearView') {
            this.setYearsOptions(this.yearsOptions[0])
            return;
        }
        if (this.currentMonth === 0) {
            this.currentMonth = 11;
            this.currentYear--;
        } else {
            this.currentMonth--;
        }
        this.calendarDays = [];
        this.leftCalendarDays = [];
        this.calendarDays = this.populateCalendar(this.currentMonth, this.currentYear);
        const [month, year] = this.decreaseCalendarLeftDate();
        this.leftCalendarDays = this.populateCalendar(month, year);

    }

    nextMonth() {
        if (!this.dayViewActive && this.monthYearViewTarget === 'yearView') {
            this.setYearsOptions(this.yearsOptions[this.yearsOptions.length -1] + this.maxYearsOptionsCount)
            return;
        }
        if (this.currentMonth === 11) {
            this.currentMonth = 0;
            this.currentYear++;
        } else {
            this.currentMonth++;
        }
        this.calendarDays = [];
        this.leftCalendarDays = [];
        this.calendarDays = this.populateCalendar(this.currentMonth, this.currentYear);
        const [month, year] = this.increaseCalendarLeftDate();
        this.leftCalendarDays = this.populateCalendar(month, year);
    }

    headerDropdownToggle(value: 'monthView' | 'yearView') {
        this.dayViewActive = false;
        this.monthYearViewTarget  = value;
        if (value === 'yearView') {
            this.setYearsOptions(this.currentYear)
        }
    }

    closeMonthYearView(isCancel: boolean = false) {
        setTimeout(() => {
            this.dayViewActive = true
            if (!isCancel) {
                this.calendarDays = this.populateCalendar(this.currentMonth, this.currentYear);
            } else {
                const date = this.parseDate(this.selectedDateString);
                this.currentYear = date?.getFullYear() ?? this.currentDate.getFullYear();
                this.currentMonth = date?.getMonth() ?? this.currentDate.getMonth();
            }
        })
    }

    populateCalendar(month: number, year: number): CalendarDays[] {
        const calendarDays = [];
        const firstDay = new Date(year, month).getDay();
        const daysInMonth = new Date(year, month + 1, 0).getDate();

        const daysInPrevMonth = new Date(year, month, 0).getDate();

        // Añadir días del mes anterior
        for (let day = firstDay - 1; day >= 0; day--) {
            calendarDays.push({
                date: new Date(year, month - 1, daysInPrevMonth - day),
                neutral: true,
                current: false,
                hasActiveEvent: this.hasActiveEventByDay(daysInPrevMonth - day, month - 1, year),
            });
        }

        // Añadir días del mes actual
        for (let day = 1; day <= daysInMonth; day++) {
            calendarDays.push({
                date: new Date(year, month, day),
                neutral: this.isNeutralByRangeDate(month, year, day),
                current: this.isCurrentDay(day, month, year),
                hasActiveEvent: this.hasActiveEventByDay(day, month, year),
            });
        }

        // Añadir días del mes siguiente
        const totalCells = calendarDays.length;
        const remainingCells = this.numDaysInRow - (totalCells % this.numDaysInRow);
        if (remainingCells < this.numDaysInRow) {
            for (let day = 1; day <= remainingCells; day++) {
                calendarDays.push({
                    date: new Date(year, month + 1, day),
                    neutral: true,
                    current: false,
                    hasActiveEvent: this.hasActiveEventByDay(day, month + 1, year),
                });
            }
        }

        return calendarDays;
    }

    selectDate(date: Date) {
        this.selectedDate = date;
        this.isDisabledSaveButton = this.isRange;

        if (this.isRange) {
            this.safeDateRangeSelected(date);
        }
    }

    isSelectedDay(date: Date): boolean {
        if(this.isRange) {
            return date.toUTCString() === this.rangeSelected?.start?.toUTCString() || date?.toDateString() === this.selectedDate?.toDateString();
        }
        return date?.toDateString() === this.selectedDate?.toDateString();
    }

    isCurrentDay(day: number, month: number, year: number): boolean {
        return day === this.currentDate.getDate() && this.currentDate.getMonth() === month && year === this.currentDate.getFullYear();
    }

    hasActiveEventByDay(day: number, month: number, year: number): boolean {
        if (!this.activeEvents.length || this.isRange) return false;
        return this.activeEvents.some((e) => {
            const eventDate = new Date(e);
            if (isNaN(eventDate.getTime())) throw Error(`${e}: no es un formato fecha UTC string valido para activeEvent`);
            return eventDate.getDate() === day && eventDate.getMonth() === month && eventDate.getFullYear() === year;
        });
    }

    handleKeydown(event: KeyboardEvent, currentFocusedIndex: number) {
        if (!this.calendarVisible || !this.isValidKey(event.key)) {
            return;
        }

        switch (event.key) {
            case 'ArrowLeft':
                currentFocusedIndex = (currentFocusedIndex - 1 + this.calendarDays.length) % this.calendarDays.length;
                break;
            case 'ArrowRight':
                currentFocusedIndex = (currentFocusedIndex + 1) % this.calendarDays.length;
                break;
            case 'ArrowUp':
                currentFocusedIndex = (currentFocusedIndex - this.numDaysInRow + this.calendarDays.length) % this.calendarDays.length;
                break;
            case 'ArrowDown':
                currentFocusedIndex = (currentFocusedIndex + this.numDaysInRow) % this.calendarDays.length;
                break;
            case 'Enter':
                this.selectDate(this.calendarDays[currentFocusedIndex].date);
                break;
            default:
                return;
        }
        event.preventDefault();
        event.stopPropagation();

        if (!this.dayButtons) {
            this.dayButtons = document.querySelectorAll('.calendar-days button');
        }

        if (this.dayButtons[currentFocusedIndex] && !this.calendarDays[currentFocusedIndex].neutral) {
            this.dayButtons[currentFocusedIndex].focus();
        }
    }

    handleCancelEvent() {
        this.calendarVisible = false;
        const date = this.parseDate(this.selectedDateString);
        if (!date) {
            this.selectedDateString = '';
            this.rangeSelected = {} as DianDatePickerRange;
            this.selectedDate = null;
            // this.resetInputError();

        }
    }

    handleSaveChangeEvent() {
        this.calendarVisible = false;
        if (this.selectedDate && !this.isRange ) {
            this.hasInputError = false;
            this.selectedDate.setHours(this.timeSelected?.hours ?? 0);
            this.selectedDate.setMinutes(this.timeSelected?.minutes ?? 0);
            this.selectedDate.setSeconds(this.timeSelected?.seconds ?? 0);

        }

        if (this.isRange) {
            this.selectedRangeStartDateString = this.rangeSelected.start.toLocaleDateString("en-GB");
            this.selectedRangeEndDateString = this.rangeSelected.end.toLocaleDateString("en-GB");
            this.value = this.rangeSelected.start.toUTCString() + ' ' + this.rangeSelected.end.toUTCString()
        } else {
            //if (this.selectedDateString && this.value ) {}
            this.selectedDateString = this.selectedDate?.toLocaleDateString("en-GB") || '';
            this.value = this.selectedDate?.toUTCString() || '';
        }
        this.onChange(this.value);
        this.assignedFormControl?.updateValueAndValidity();
        this.dateSelected.emit(this.value);
    }

    handleTimeSelected(event: Omit<TimeSelected, 'format'>) {
        this.timeSelected = event;
    }

    handleInputChange(event: InputEvent) {
        const el = event.target as HTMLInputElement;
        this.selectedDateString = el.value;
        const date = this.parseDate(this.selectedDateString);
        if (date) {
            this.selectedDate = date;
        } else {
            this.selectedDate = null;
            this.hasInputError = this.selectedDateString.length > 0;
        }
        this.handleSaveChangeEvent();
    }

    handleBlurEvent() {
        this.onTouch();
        this.onChange(this.selectedDate?.toUTCString() ?? '');
    }

    setCurrentMonthByIndex(index: number) {
        this.currentMonth = index;
    }

    markDaysInRange(range: DianDatePickerRange) {
        if (Object.values(range).length !== 2 || !this.isRange) return;
        this.calendarDays.forEach((day) => {
            day.isDayInRange = range.start?.getTime() < day.date.getTime() && range.end?.getTime() > day.date.getTime();
            day.isRangeStart = range.start?.toUTCString() === day.date.toUTCString();
            day.isRangeEnd = range.end?.toUTCString() === day.date.toUTCString();
        })
        if (this.calendarType === DatePickerType.DOUBLE) {
            this.leftCalendarDays.forEach((day) => {
                day.isDayInRange = range.start?.getTime() < day.date.getTime() && range.end?.getTime() > day.date.getTime();
                day.isRangeStart = range.start?.toUTCString() === day.date.toUTCString();
                day.isRangeEnd = range.end?.toUTCString() === day.date.toUTCString();
            })
        }
    }

    clearDateRangeSelected() {
        this.rangeSelected = {} as DianDatePickerRange;
        this.selectedDate = null;
        this.selectedRangeStartDateString = '';
        this.selectedRangeEndDateString = '';
        this.isDisabledSaveButton = true;
    }

    isDisabledSaveChangeButton(): boolean {
        if (this.isRange) {
            const temp = Object.values(this.rangeSelected).length !== 2;
            return temp;
        }
        return true;

    }

    /**
     * The function `parseDate` takes a string input in the format "day/month/year" and returns a Date
     * object if the input is a valid date, otherwise it returns null.
     * @param {string} input
     * @returns {Date} object
     */
    private parseDate(input: string, divider: string = '/'): Date | null {
        const parts = input.split(divider);

        if (parts.length !== 3) {
            return null;
        }

        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1; // Los meses son de 0 a 11
        const year = parseInt(parts[2], 10);

        const date = new Date(year, month, day);

        // Verificar si la fecha es válida
        if (date.getFullYear() === year && date.getMonth() === month && date.getDate() === day) {
            return date;
        } else {
            return null;
        }
    }

    private increaseCalendarLeftDate(): number[] {
        let month = this.currentMonth;
        let year = this.currentYear;
        if (this.currentMonth === 11) {
            month = 0;
            year++;
        } else {
            month++
        }
        return [month, year];
    }

    private decreaseCalendarLeftDate(): number[] {
        let month = this.currentMonth;
        let year = this.currentYear;
        if (this.currentMonth === 0) {
            month = 11;
            year--;
        } else {
            month--;
        }
        return [month, year];
    }

    private isValidKey(keyName: string): boolean {
        return ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Enter'].includes(keyName);
    }

    private setActiveDate(date?: Date) {
        let newDate;
        if (date) {
            newDate = date;
        } else {
            newDate = new Date();
        }

        // Retorna el mes (0–11) para la fecha especificada acorde a la hora local.
        this.currentMonth = newDate.getMonth();
        // Retorna el año (4 dígitos para años de 4 dígitos) para la fecha especificada acorde a la hora local.
        this.currentYear = newDate.getFullYear();
        //  Retorna el día del mes (1–31) para la fecha especificada acorde a la hora local.
        this.currentDayDate = newDate.getDate();
    }

    private resetInputError() {
        this.inputElement.nativeElement.classList.remove('invalid');
        this.hasInputError = false;
    }

    private autoCloseCalendarModal() {
        this.renderer.listen('window', 'click',(e: MouseEvent)=>{
                const target = e.target as HTMLElement;
                if (!this.datePicker.nativeElement.contains(target)) {
                    this.calendarVisible = false;
                }
       });
    }

    private safeDateRangeSelected(date: Date) {
        if (!this.rangeSelected?.start || date.getTime() < this.rangeSelected?.start?.getTime()) {
            Object.assign(this.rangeSelected, { start: date, end: null });
            this.isDisabledSaveButton = true;
        } else {
            Object.assign(this.rangeSelected, { end: date })
            this.isDisabledSaveButton = false;
        }
        this.markDaysInRange(this.rangeSelected);
    }

    private setYearsOptions(start: number) {
        this.yearsOptions = [];
        const maxRange = start + 1;
        for (let index = maxRange - this.maxYearsOptionsCount; index <= maxRange; index++) {
            this.yearsOptions.push(index);
        }
    }

    private isNeutralByRangeDate(month: number, year: number, date: number) {
        const evalDateTime= new Date(year, month, date).getTime();
        const maxLimit = this.maxDateLimit?.getTime();
        const minLimit = this.minDateLimit?.getTime();

        if (!isNaN(minLimit) && !isNaN(maxLimit)) {
            console.log('first', evalDateTime > minLimit && evalDateTime < maxLimit)
            return evalDateTime < minLimit || evalDateTime > maxLimit;

        } else if(!isNaN(minLimit)) {
            console.log('second', evalDateTime > minLimit)
            return evalDateTime < minLimit;

        } else if (!isNaN(maxLimit)) {
            console.log('tercero', evalDateTime < maxLimit )
            return evalDateTime > maxLimit;
        }

        return false;
    }
}
